import { css, IObjectWithKey, ISelection, MarqueeSelection, Selection, SelectionZone, Stack, StackItem } from '@fluentui/react'
import { AccordionPanel, makeStyles, Switch, SwitchOnChangeData, Tooltip } from '@fluentui/react-components'
import LineItemSelector from '../../LineItemSelector'
import * as React from 'react'
import { ReactElement, useEffect, useMemo, useRef } from 'react'
import { createLineItemMap } from '../../LineItemGroups'
import { LineItemGroupDialogProps } from './LineItemGroupDialog'
import { LineItemEntity } from '../../../../../../hooks/finance/invoice/useLineItemEntities'
import { useTranslation } from 'react-i18next'
import { LineItemType } from '../../../../../../config/finance/invoiceConfig'
import { useBoolean } from '@fluentui/react-hooks'

const useStyles = makeStyles({
    lineItemSelector: { marginLeft: '24px' },
})

export default function LineItemPanel(props: LineItemGroupDialogProps): ReactElement {
    const styles = useStyles()
    const { t: translate } = useTranslation()

    const [initialized, { setTrue: setInitialized }] = useBoolean(false)

    const availableEntities = useMemo(
        () =>
            createLineItemMap(
                [
                    ...[...props.lineItemEntities.orphans.values()].flatMap(entities => entities.map(entity => entity.id)),
                    ...props.values.lineItems.map(lineItem => lineItem.entityId),
                    props.values.fixedPriceId,
                ],
                props.meta.data,
                true,
            ),
        [props.lineItemEntities.orphans, props.lineItemGroup.lineItems],
    )
    const setUpdateValuesRef = useRef(props.updateValues)

    const selection = useMemo<ISelection<LineItemEntity & IObjectWithKey>>(() => {
        return new Selection({
            items: [...availableEntities.values()].flatMap(entities => entities),
            // The onSelectionChanged function is defined only once, when the Selection object is first created, and it
            // captures the updateValues variable as it exists at that time.
            onSelectionChanged: () => {
                const selectedEntityID = selection.getItems().find((value, index) => selection['_anchoredIndex'] === index)?.id

                selection.setChangeEvents(false, true)

                const fixedPriceId = selection
                    .getSelection()
                    .map(item => props.lineItemEntities.map(item))
                    .find(item => item.type === LineItemType.FixedPrice && item.entityId === selectedEntityID)?.entityId

                if (fixedPriceId) {
                    availableEntities
                        .get(LineItemType.FixedPrice)
                        .map(item => props.lineItemEntities.map(item))
                        .filter(item => item.entityId !== fixedPriceId)
                        .forEach(fixedPrice => selection.setKeySelected(fixedPrice.entityId, false, false))
                }

                const items = selection
                    .getSelection()
                    .map(item => props.lineItemEntities.map(item))
                    .filter(item => item.type !== LineItemType.FixedPrice)
                    .filter(Boolean)

                setUpdateValuesRef.current({ lineItems: items, fixedPriceId: fixedPriceId })
                selection.setChangeEvents(true, false)
            },
            getKey: entity => entity.id,
        })
    }, [availableEntities])

    useEffect(() => {
        // Keep track of the latest setUpdateValuesRef, because otherwise onSelectionChanged stick to the initial state
        // of setUpdateValuesRef and use outdated values.
        setUpdateValuesRef.current = props.updateValues
    }, [props.values])

    useEffect(() => {
        if (initialized) {
            return
        }

        selection.setChangeEvents(false, true)

        props.values.lineItems.forEach(lineItem => {
            selection.setKeySelected(lineItem.entityId, true, false)
        })
        if (props.values.fixedPriceId) {
            selection.setKeySelected(props.values.fixedPriceId, true, false)
        }

        setInitialized()
        selection.setChangeEvents(true, false)
    }, [props.values.lineItems])

    return (
        <AccordionPanel>
            <MarqueeSelection selection={selection} isDraggingConstrainedToRoot>
                <SelectionZone selection={selection} toggleWithoutModifierPressed selectionPreservedOnEmptyClick>
                    <Stack>
                        <Tooltip
                            relationship="description"
                            content={translate('invoice.edit.form.invoicegroup.general.showstatement.tooltip')}
                            withArrow
                        >
                            <Switch
                                checked={props.values.showStatement}
                                onChange={(_, data: SwitchOnChangeData) => props.updateValues({ showStatement: data.checked })}
                                label={translate('invoice.edit.form.invoicegroup.general.showstatement')}
                            />
                        </Tooltip>
                        <StackItem className={css(styles.lineItemSelector)}>
                            <LineItemSelector
                                {...props}
                                entities={availableEntities}
                                selection={selection}
                                showPrice
                                findQuantity={props.lineItemEntities.findQuantity}
                                getPrice={props.lineItemEntities.getPrice}
                            />
                        </StackItem>
                    </Stack>
                </SelectionZone>
            </MarqueeSelection>
        </AccordionPanel>
    )
}
